import React, { useEffect, useId, useLayoutEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import clsx from 'clsx';
import { X } from 'lucide-react';

// The one modal for the app. Before this, most pages built their own: no Escape key, focus left
// behind on the page, no dialog role, so keyboard and screen-reader users could get stuck.
// This one: role=dialog + aria-modal, labelled by its title, Escape and backdrop close (unless
// `dismissable` is false), focus moves in on open, stays inside while open (Tab wraps), and goes
// back to whatever opened it on close; the page behind stops scrolling.
//
//   <Dialog open={open} onClose={close} title="Edit rate" footer={<>…buttons…</>}>…</Dialog>
//
// `size`: sm 420 · md 560 · lg 720 · xl 960 · full (drawer-height sheet on phones either way).
const WIDTH = { sm: 'sm:max-w-[420px]', md: 'sm:max-w-[560px]', lg: 'sm:max-w-[720px]', xl: 'sm:max-w-[960px]', full: 'sm:max-w-[1200px]' };
const FOCUSABLE = 'a[href],button:not([disabled]),textarea:not([disabled]),input:not([disabled]):not([type="hidden"]),select:not([disabled]),[tabindex]:not([tabindex="-1"])';

export default function Dialog({
  open, onClose, title, description, children, footer, size = 'md',
  dismissable = true, initialFocusRef, className, bodyClassName, hideClose = false,
}) {
  const panelRef = useRef(null);
  const returnTo = useRef(null);
  const titleId = useId();
  const descId = useId();
  // Kept in a ref so an inline onClose (a new function every render) doesn't re-run the focus
  // effect on each keystroke and pull focus out of the field being typed in.
  const onCloseRef = useRef(onClose);
  useLayoutEffect(() => { onCloseRef.current = onClose; });
  const close = () => { if (onCloseRef.current) onCloseRef.current(); };

  useEffect(() => {
    if (!open) return undefined;
    returnTo.current = document.activeElement;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const t = setTimeout(() => {
      const panel = panelRef.current;
      if (!panel) return;
      const target = (initialFocusRef && initialFocusRef.current)
        || panel.querySelector('[data-autofocus]')
        || panel.querySelector('input:not([type="hidden"]):not([disabled]),select:not([disabled]),textarea:not([disabled])')
        || panel.querySelector(FOCUSABLE)
        || panel;
      target.focus({ preventScroll: true });
    }, 0);
    const onKey = (e) => {
      if (e.key === 'Escape' && dismissable) {
        // A confirm opened from inside this dialog handles its own Escape.
        if (document.querySelector('[role="alertdialog"][aria-modal="true"]')) return;
        e.stopPropagation(); close(); return;
      }
      if (e.key !== 'Tab' || !panelRef.current) return;
      const items = Array.from(panelRef.current.querySelectorAll(FOCUSABLE)).filter((el) => el.offsetParent !== null);
      if (!items.length) { e.preventDefault(); return; }
      const first = items[0]; const last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    };
    document.addEventListener('keydown', onKey, true);
    return () => {
      clearTimeout(t);
      document.removeEventListener('keydown', onKey, true);
      document.body.style.overflow = prevOverflow;
      const back = returnTo.current;
      if (back && typeof back.focus === 'function') back.focus({ preventScroll: true });
    };
  }, [open, dismissable, initialFocusRef]);

  if (!open) return null;
  return createPortal(
    <div className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center sm:p-4">
      <div className="absolute inset-0 bg-slate-900/40 animate-fade-in motion-reduce:animate-none"
        onMouseDown={() => { if (dismissable) close(); }} aria-hidden="true" />
      <div
        ref={panelRef} role="dialog" aria-modal="true" tabIndex={-1}
        aria-labelledby={title ? titleId : undefined} aria-describedby={description ? descId : undefined}
        className={clsx('relative w-full bg-white shadow-modal flex flex-col max-h-[92vh] outline-none',
          'rounded-t-2xl sm:rounded-2xl animate-fade-up motion-reduce:animate-none', WIDTH[size] || WIDTH.md, className)}
      >
        {(title || !hideClose) && (
          <div className="flex items-start justify-between gap-4 px-5 sm:px-6 pt-5 pb-3">
            <div className="min-w-0">
              {title && <h2 id={titleId} className="text-[17px] font-semibold leading-snug tracking-[-0.01em] text-slate-900">{title}</h2>}
              {description && <p id={descId} className="mt-1 text-sm leading-relaxed text-slate-500">{description}</p>}
            </div>
            {!hideClose && (
              <button type="button" onClick={onClose} aria-label="Close"
                className="-mr-2 -mt-1 shrink-0 rounded-lg p-2 text-slate-400 hover:bg-slate-100 hover:text-slate-700">
                <X size={18} />
              </button>
            )}
          </div>
        )}
        <div className={clsx('min-h-0 flex-1 overflow-y-auto px-5 sm:px-6 pb-5', bodyClassName)}>{children}</div>
        {footer && (
          <div className="flex flex-wrap items-center justify-end gap-2 border-t border-slate-100 bg-slate-50/60 px-5 sm:px-6 py-3 rounded-b-2xl">
            {footer}
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}
